import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Users, DollarSign, TrendingUp, Activity,
  BarChart3, Search, Filter, RefreshCw, Crown, Ban, CheckCircle, 
  AlertTriangle, Settings, UserCog, CreditCard, XCircle, Clock,
  Shield, Eye, FileText, User, Info, Lock, KeyRound, Mail, UserCheck, UserX, RotateCcw, Paperclip, X as XIcon
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { API, getAuthHeaders } from '../utils/api';

// User role options
const USER_ROLES = ['user', 'support', 'developer', 'ceo', 'owner', 'admin'];
const USER_STATUSES = ['active', 'inactive', 'suspended', 'banned'];

export default function AdminDashboard() {
  // Admin PIN authentication state
  const [adminAuthenticated, setAdminAuthenticated] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  const [adminSession, setAdminSession] = useState(null);
  const [pinVerifying, setPinVerifying] = useState(false);
  const [resendingPin, setResendingPin] = useState(false);
  
  const [signals, setSignals] = useState([]);
  const [users, setUsers] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [kycSubmissions, setKycSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  // Support tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [ticketSending, setTicketSending] = useState(false);
  const [ticketAttachment, setTicketAttachment] = useState(null);
  const [ticketUploading, setTicketUploading] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [kycPin, setKycPin] = useState('');
  const [kycPinDialogOpen, setKycPinDialogOpen] = useState(false);
  const [pendingKycId, setPendingKycId] = useState(null);
  const [setPinDialogOpen, setSetPinDialogOpen] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [hasKycPin, setHasKycPin] = useState(false);
  
  // User edit state
  const [editingUserRole, setEditingUserRole] = useState('');
  const [editingUserStatus, setEditingUserStatus] = useState('');
  
  const [formData, setFormData] = useState({
    market: '',
    direction: 'buy',
    entry_zone: '',
    stop_loss: '',
    take_profit: '',
    risk_reward: '',
    confidence: 'medium',
    explanation: ''
  });

  // Check for existing admin session on mount
  useEffect(() => {
    const storedSession = localStorage.getItem('adminSession');
    if (storedSession) {
      verifyStoredSession(storedSession);
    }
  }, []);

  useEffect(() => {
    if (adminAuthenticated) {
      fetchAllData();
    }
  }, [adminAuthenticated]);

  const verifyStoredSession = async (session) => {
    try {
      const response = await axios.get(`${API}/admin/check-pin-session?admin_session=${session}`, {
        withCredentials: true
      });
      
      if (response.data.valid) {
        setAdminSession(session);
        setAdminAuthenticated(true);
      } else {
        localStorage.removeItem('adminSession');
      }
    } catch (error) {
      localStorage.removeItem('adminSession');
    }
  };

  const verifyAdminPin = async () => {
    if (!adminPin || adminPin.length !== 6) {
      toast.error('Please enter the 6-digit PIN');
      return;
    }
    
    setPinVerifying(true);
    try {
      const response = await axios.post(`${API}/admin/verify-pin`, {
        pin: adminPin
      }, { withCredentials: true, headers: getAuthHeaders() });
      
      if (response.data.status === 'success') {
        setAdminSession(response.data.admin_session);
        setAdminAuthenticated(true);
        localStorage.setItem('adminSession', response.data.admin_session);
        toast.success('PIN verified! Welcome to Admin Dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired PIN');
    } finally {
      setPinVerifying(false);
      setAdminPin('');
    }
  };

  const resendAdminPin = async () => {
    setResendingPin(true);
    try {
      const response = await axios.post(`${API}/admin/resend-pin`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(response.data.message || 'New PIN sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send PIN');
    } finally {
      setResendingPin(false);
    }
  };

  const logoutAdmin = () => {
    setAdminAuthenticated(false);
    setAdminSession(null);
    localStorage.removeItem('adminSession');
    toast.info('Logged out from admin dashboard');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [signalsRes, usersRes, analyticsRes, refundsRes, kycRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/signals`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/admin/analytics`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/admin/refunds`, { withCredentials: true, headers: getAuthHeaders() }).catch(() => ({ data: { refunds: [] } })),
        axios.get(`${API}/admin/kyc`, { withCredentials: true, headers: getAuthHeaders() }).catch(() => ({ data: { submissions: [] } })),
        axios.get(`${API}/admin/tickets`, { withCredentials: true, headers: getAuthHeaders() }).catch(() => ({ data: { tickets: [] } })),
      ]);
      
      const signalsData = signalsRes.data.signals || signalsRes.data;
      setSignals(Array.isArray(signalsData) ? signalsData : []);
      setUsers(usersRes.data.users || []);
      setAnalytics(analyticsRes.data);
      setRefunds(refundsRes.data.refunds || []);
      setKycSubmissions(kycRes.data.submissions || []);
      setTickets(ticketsRes.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/signals`, formData, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Signal created successfully!');
      setDialogOpen(false);
      fetchAllData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create signal');
    }
  };

  const resetForm = () => {
    setFormData({
      market: '', direction: 'buy', entry_zone: '', stop_loss: '',
      take_profit: '', risk_reward: '', confidence: 'medium', explanation: ''
    });
  };

  const handleDeleteSignal = async (signalId) => {
    if (!window.confirm('Delete this signal?')) return;
    try {
      await axios.delete(`${API}/signals/${signalId}`, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Signal deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete signal');
    }
  };

  const handleBanUser = async (userId, isBanned) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/${isBanned ? 'unban' : 'ban'}`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(`User ${isBanned ? 'unbanned' : 'banned'}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('User updated successfully');
      setUserDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleActivateSubscription = async (userId, plan, days) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/activate-subscription?plan=${plan}&days=${days}`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(`${plan} activated for ${days} days`);
      setUserDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to activate subscription');
    }
  };

  const handleCancelSubscription = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/cancel-subscription`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Subscription cancelled');
      setUserDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleApproveRefund = async (refundId) => {
    try {
      await axios.post(`${API}/admin/refunds/${refundId}/approve`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Refund approved');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve refund');
    }
  };

  const handleRejectRefund = async (refundId) => {
    try {
      await axios.post(`${API}/admin/refunds/${refundId}/reject`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Refund rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject refund');
    }
  };

  const openKycDialog = async (kyc) => {
    // First check if this KYC requires PIN (new encrypted format)
    try {
      const metaRes = await axios.get(`${API}/admin/kyc/${kyc.kyc_id}`, { withCredentials: true, headers: getAuthHeaders() });
      if (metaRes.data.requires_pin || metaRes.data.encryption_version !== 'legacy') {
        // Need PIN to access
        setPendingKycId(kyc.kyc_id);
        setKycPinDialogOpen(true);
      } else {
        // Legacy unencrypted data - direct access (for old submissions)
        setSelectedKyc(metaRes.data);
        setKycDialogOpen(true);
      }
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('PIN not set')) {
        toast.error('Please set your KYC access PIN first');
        setSetPinDialogOpen(true);
      } else {
        toast.error('Failed to load KYC details');
      }
    }
  };

  const accessKycWithPin = async () => {
    if (!kycPin || kycPin.length < 4) {
      toast.error('Please enter your 4-8 digit PIN');
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/kyc/${pendingKycId}/access`, 
        { admin_pin: kycPin }, 
        { withCredentials: true, headers: getAuthHeaders() }
      );
      setSelectedKyc(res.data);
      setKycPinDialogOpen(false);
      setKycDialogOpen(true);
      setKycPin('');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Invalid PIN');
      } else if (error.response?.status === 400) {
        toast.error('Please set your KYC access PIN first');
        setKycPinDialogOpen(false);
        setSetPinDialogOpen(true);
      } else {
        toast.error('Failed to access KYC data');
      }
    }
  };

  const setAdminKycPin = async () => {
    if (!newPin || newPin.length < 4 || newPin.length > 8 || !/^\d+$/.test(newPin)) {
      toast.error('PIN must be 4-8 digits');
      return;
    }
    try {
      await axios.post(`${API}/admin/set-kyc-pin?new_pin=${newPin}`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('KYC access PIN set successfully');
      setSetPinDialogOpen(false);
      setNewPin('');
      setHasKycPin(true);
    } catch (error) {
      toast.error('Failed to set PIN');
    }
  };

  const resetKycPin = async () => {
    try {
      await axios.delete(`${API}/admin/reset-kyc-pin`, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('KYC access PIN has been reset. You can now set a new PIN.');
      setHasKycPin(false);
      setSetPinDialogOpen(true);
    } catch (error) {
      toast.error('Failed to reset PIN');
    }
  };

  const handleApproveKyc = async (kycId) => {
    try {
      await axios.post(`${API}/admin/kyc/${kycId}/review`, { status: 'approved' }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('KYC approved');
      setKycDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve KYC');
    }
  };

  const handleRejectKyc = async (kycId) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await axios.post(`${API}/admin/kyc/${kycId}/review`, { 
        status: 'rejected', 
        rejection_reason: rejectionReason 
      }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('KYC rejected');
      setKycDialogOpen(false);
      setRejectionReason('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject KYC');
    }
  };

  const openUserDialog = (user) => {
    setSelectedUser(user);
    setUserDialogOpen(true);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = userFilter === 'all' ||
                         (userFilter === 'pro' && user.subscription_plan) ||
                         (userFilter === 'free' && !user.subscription_plan) ||
                         (userFilter === 'banned' && user.banned);
    return matchesSearch && matchesFilter;
  });

  const pendingRefunds = refunds.filter(r => r.status === 'pending');
  const pendingKycs = kycSubmissions.filter(k => k.status === 'pending');
  const filteredTickets = tickets.filter(t => ticketStatusFilter === 'all' || t.status === ticketStatusFilter);
  const openTicketCount = tickets.filter(t => ['open', 'in_progress'].includes(t.status)).length;

  const refreshTickets = async () => {
    try {
      const r = await axios.get(`${API}/admin/tickets`, { withCredentials: true, headers: getAuthHeaders() });
      setTickets(r.data.tickets || []);
      if (selectedTicket) {
        const fresh = (r.data.tickets || []).find(t => t.ticket_id === selectedTicket.ticket_id);
        if (fresh) setSelectedTicket(fresh);
      }
    } catch (e) {
      // ignore
    }
  };

  const handleTicketReply = async () => {
    if (!selectedTicket || (!ticketReply.trim() && !ticketAttachment)) return;
    setTicketSending(true);
    try {
      // If there's an attachment, upload it (with optional message)
      if (ticketAttachment) {
        setTicketUploading(true);
        const fd = new FormData();
        fd.append('file', ticketAttachment);
        fd.append('message', ticketReply.trim() || '');
        await axios.post(
          `${API}/admin/tickets/${selectedTicket.ticket_id}/attachments`,
          fd,
          { withCredentials: true, headers: { ...getAuthHeaders() } }
        );
        setTicketAttachment(null);
        setTicketUploading(false);
      } else {
        await axios.post(
          `${API}/admin/tickets/${selectedTicket.ticket_id}/reply`,
          { message: ticketReply.trim() },
          { withCredentials: true, headers: getAuthHeaders() }
        );
      }
      setTicketReply('');
      toast.success('Reply sent');
      await refreshTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setTicketSending(false);
      setTicketUploading(false);
    }
  };

  const handleTicketStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    try {
      await axios.put(
        `${API}/admin/tickets/${selectedTicket.ticket_id}/status?status=${newStatus}`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success(`Ticket ${newStatus}`);
      await refreshTickets();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  // Show PIN authentication screen if not authenticated
  if (!adminAuthenticated) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Card className="bg-slate-900/50 border-white/10 p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-slate-400 mt-2">Enter your 6-digit PIN to access</p>
          </div>
          
          <div className="space-y-6">
            <div>
              <Label className="text-sm text-slate-400">Admin PIN</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="text"
                  maxLength={6}
                  value={adminPin}
                  onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="bg-zinc-900 border-zinc-800 text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="admin-pin-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                <KeyRound className="w-3 h-3 inline mr-1" />
                PIN is sent to your developer email every 24 hours
              </p>
            </div>
            
            <Button 
              onClick={verifyAdminPin}
              disabled={pinVerifying || adminPin.length !== 6}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="verify-pin-btn"
            >
              {pinVerifying ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Verify & Access
                </>
              )}
            </Button>
            
            <div className="text-center">
              <p className="text-sm text-slate-500 mb-2">Didn't receive PIN?</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={resendAdminPin}
                disabled={resendingPin}
                className="border-zinc-700"
              >
                {resendingPin ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend PIN to Email
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-zinc-800">
            <div className="flex items-start gap-3 text-xs text-slate-500">
              <Shield className="w-4 h-4 mt-0.5 text-amber-500" />
              <p>
                For security, the admin PIN changes every 24 hours and is sent only to the registered developer email.
                This protects against unauthorized access.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-slate-400">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold font-['Manrope'] mb-2">Admin Dashboard</h1>
          <p className="text-slate-400">Manage users, signals, memberships, and refunds</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={logoutAdmin} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
            <Lock className="w-4 h-4 mr-2" />
            Lock Dashboard
          </Button>
          <Button variant="outline" onClick={fetchAllData} className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-5 h-5 mr-2" />
                Create Signal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121214] border-zinc-800 max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Signal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Market</Label>
                    <Input required value={formData.market} onChange={(e) => setFormData({...formData, market: e.target.value})}
                      placeholder="XAUUSD" className="bg-zinc-900 border-zinc-800 mt-1" />
                  </div>
                  <div>
                    <Label>Direction</Label>
                    <Select value={formData.direction} onValueChange={(v) => setFormData({...formData, direction: v})}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121214] border-zinc-800">
                        <SelectItem value="buy">Buy</SelectItem>
                        <SelectItem value="sell">Sell</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Entry Zone</Label>
                    <Input required value={formData.entry_zone} onChange={(e) => setFormData({...formData, entry_zone: e.target.value})}
                      placeholder="2650-2655" className="bg-zinc-900 border-zinc-800 mt-1" />
                  </div>
                  <div>
                    <Label>Stop Loss</Label>
                    <Input required value={formData.stop_loss} onChange={(e) => setFormData({...formData, stop_loss: e.target.value})}
                      placeholder="2645" className="bg-zinc-900 border-zinc-800 mt-1" />
                  </div>
                  <div>
                    <Label>Take Profit</Label>
                    <Input required value={formData.take_profit} onChange={(e) => setFormData({...formData, take_profit: e.target.value})}
                      placeholder="2670" className="bg-zinc-900 border-zinc-800 mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Risk:Reward</Label>
                    <Input required value={formData.risk_reward} onChange={(e) => setFormData({...formData, risk_reward: e.target.value})}
                      placeholder="1:3" className="bg-zinc-900 border-zinc-800 mt-1" />
                  </div>
                  <div>
                    <Label>Confidence</Label>
                    <Select value={formData.confidence} onValueChange={(v) => setFormData({...formData, confidence: v})}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121214] border-zinc-800">
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Explanation</Label>
                  <Textarea required value={formData.explanation} onChange={(e) => setFormData({...formData, explanation: e.target.value})}
                    placeholder="Technical analysis..." className="bg-zinc-900 border-zinc-800 mt-1 min-h-[80px]" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-zinc-700">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">Create Signal</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-800">
            <BarChart3 className="w-4 h-4 mr-2" />Overview
          </TabsTrigger>
          <TabsTrigger value="signals" className="data-[state=active]:bg-zinc-800">
            <TrendingUp className="w-4 h-4 mr-2" />Signals
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-zinc-800">
            <Users className="w-4 h-4 mr-2" />Users
          </TabsTrigger>
          <TabsTrigger value="kyc" className="data-[state=active]:bg-zinc-800 relative">
            <Shield className="w-4 h-4 mr-2" />KYC
            {pendingKycs.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full text-xs flex items-center justify-center">
                {pendingKycs.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="refunds" className="data-[state=active]:bg-zinc-800 relative">
            <CreditCard className="w-4 h-4 mr-2" />Refunds
            {pendingRefunds.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                {pendingRefunds.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="tickets" data-testid="admin-tickets-tab" className="data-[state=active]:bg-zinc-800 relative">
            <Mail className="w-4 h-4 mr-2" />Tickets
            {openTicketCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full text-xs flex items-center justify-center">
                {openTicketCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#121214] border-zinc-800 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Total Users</p>
                  <p className="text-2xl font-bold">{analytics?.total_users || 0}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-[#121214] border-zinc-800 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Crown className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Pro Subscribers</p>
                  <p className="text-2xl font-bold">{analytics?.pro_users || 0}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-[#121214] border-zinc-800 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Monthly Revenue</p>
                  <p className="text-2xl font-bold">${analytics?.monthly_revenue || 0}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-[#121214] border-zinc-800 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Activity className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Active Signals</p>
                  <p className="text-2xl font-bold">{analytics?.active_signals || signals.length}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Broadcast Test Panel */}
          <Card className="bg-[#121214] border-zinc-800 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Signal Broadcast Health</h3>
                <p className="text-sm text-slate-400">Test Telegram + Discord and push the daily log to your logs channel.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="admin-broadcast-test-btn"
                onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/admin/broadcast/test`, {}, { withCredentials: true, headers: getAuthHeaders() });
                    const d = r.data;
                    toast.success(`Test sent — Telegram: ${d.telegram_sent ? '✓' : '✗'} · Discord: ${d.discord_sent ? '✓' : '✗'}`);
                  } catch (e) {
                    toast.error('Broadcast test failed');
                  }
                }}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <TrendingUp className="w-4 h-4 mr-2" /> Send Test Signal
              </Button>
              <Button
                data-testid="admin-daily-log-btn"
                variant="outline"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/broadcast/daily-log`, {}, { withCredentials: true, headers: getAuthHeaders() });
                    toast.success('Daily log pushed to Discord logs channel');
                  } catch (e) {
                    toast.error('Daily log push failed');
                  }
                }}
                className="border-zinc-700"
              >
                <BarChart3 className="w-4 h-4 mr-2" /> Push Daily Log Now
              </Button>
            </div>
          </Card>

          {/* Recent Subscriptions */}
          <Card className="bg-[#121214] border-zinc-800 p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Subscriptions</h3>
            <div className="space-y-3">
              {(analytics?.recent_subscriptions || []).slice(0, 5).map((sub, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <Crown className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-medium">{sub.email}</p>
                      <p className="text-xs text-slate-500">{sub.plan}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-emerald-400">${sub.amount}</p>
                    <p className="text-xs text-slate-500">{new Date(sub.date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
              {(!analytics?.recent_subscriptions || analytics.recent_subscriptions.length === 0) && (
                <p className="text-slate-500 text-center py-4">No recent subscriptions</p>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Signals Tab */}
        <TabsContent value="signals">
          <Card className="bg-[#121214] border-zinc-800 p-6">
            <h2 className="text-xl font-semibold mb-4">All Signals ({signals.length})</h2>
            <div className="space-y-3">
              {signals.map((signal) => (
                <div key={signal.signal_id} className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-mono font-semibold text-lg">{signal.market}</p>
                      <Badge className={signal.direction === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                        {signal.direction?.toUpperCase()}
                      </Badge>
                      {signal.is_auto_generated && <Badge className="bg-purple-500/20 text-purple-400">AI</Badge>}
                    </div>
                    <p className="text-sm text-slate-400">{signal.explanation?.substring(0, 80)}...</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteSignal(signal.signal_id)} className="text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="bg-[#121214] border-zinc-800 p-6">
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-800" />
              </div>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-800">
                  <Filter className="w-4 h-4 mr-2" /><SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121214] border-zinc-800">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="banned">Banned</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border border-zinc-800 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">User</TableHead>
                    <TableHead className="text-slate-400">Plan</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Joined</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.user_id} className="border-zinc-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full flex items-center justify-center text-sm font-bold">
                            {user.name?.[0] || 'U'}
                          </div>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.subscription_plan ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400">
                            <Crown className="w-3 h-3 mr-1" />{user.subscription_plan.replace('_', ' ')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-zinc-700 text-slate-400">Free</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.banned ? (
                          <Badge className="bg-red-500/20 text-red-400">Banned</Badge>
                        ) : (
                          <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-400">{new Date(user.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openUserDialog(user)} className="text-blue-400 hover:bg-blue-500/10">
                            <UserCog className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleBanUser(user.user_id, user.banned)}
                            className={user.banned ? "text-emerald-400 hover:bg-emerald-500/10" : "text-red-400 hover:bg-red-500/10"}>
                            {user.banned ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-sm text-slate-500 mt-4">Showing {filteredUsers.length} of {users.length} users</p>
          </Card>
        </TabsContent>

        {/* Refunds Tab */}
        <TabsContent value="refunds">
          <Card className="bg-[#121214] border-zinc-800 p-6">
            <h2 className="text-xl font-semibold mb-4">Refund Requests</h2>
            
            {refunds.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No refund requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {refunds.map((refund) => (
                  <div key={refund.refund_id} className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        refund.status === 'pending' ? 'bg-amber-500/20' :
                        refund.status === 'approved' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                      }`}>
                        {refund.status === 'pending' ? <Clock className="w-5 h-5 text-amber-400" /> :
                         refund.status === 'approved' ? <CheckCircle className="w-5 h-5 text-emerald-400" /> :
                         <XCircle className="w-5 h-5 text-red-400" />}
                      </div>
                      <div>
                        <p className="font-medium">{refund.user_email}</p>
                        <p className="text-sm text-slate-400">${refund.amount} • {new Date(refund.created_at).toLocaleDateString()}</p>
                        {refund.reason && <p className="text-xs text-slate-500 italic mt-1">"{refund.reason}"</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={
                        refund.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        refund.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                      }>
                        {refund.status}
                      </Badge>
                      {refund.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleApproveRefund(refund.refund_id)}
                            className="bg-emerald-600 hover:bg-emerald-700 h-8">
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleRejectRefund(refund.refund_id)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8">
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* KYC Tab */}
        <TabsContent value="kyc" className="space-y-6" data-testid="admin-kyc-tab">
          <Card className="bg-[#121214] border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-amber-400" />
                <h2 className="text-xl font-semibold">KYC Verifications</h2>
                <Badge className="bg-amber-500/20 text-amber-400" data-testid="kyc-pending-count">{pendingKycs.length} pending</Badge>
              </div>
              <Select defaultValue="pending">
                <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800" data-testid="kyc-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121214] border-zinc-800">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {kycSubmissions.length === 0 ? (
              <div className="text-center py-12" data-testid="kyc-empty-state">
                <Shield className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400">No KYC submissions yet</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="kyc-submissions-list">
                {kycSubmissions.map((kyc) => (
                  <div 
                    key={kyc.kyc_id} 
                    className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-colors"
                    data-testid={`kyc-submission-${kyc.kyc_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        kyc.status === 'pending' ? 'bg-amber-500/20' :
                        kyc.status === 'approved' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                      }`}>
                        {kyc.status === 'pending' ? <Clock className="w-5 h-5 text-amber-400" /> :
                         kyc.status === 'approved' ? <CheckCircle className="w-5 h-5 text-emerald-400" /> :
                         <XCircle className="w-5 h-5 text-red-400" />}
                      </div>
                      <div>
                        <p className="font-medium" data-testid="kyc-user-name">{kyc.full_name || kyc.user_email}</p>
                        <p className="text-sm text-slate-400">
                          {kyc.document_type?.replace('_', ' ')} • {kyc.country} • {new Date(kyc.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={
                        kyc.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        kyc.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                      } data-testid="kyc-status-badge">
                        {kyc.status}
                      </Badge>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => openKycDialog(kyc)}
                        className="border-zinc-700 hover:bg-zinc-800"
                        data-testid="kyc-review-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Review
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets">
          <Card className="bg-[#121214] border-zinc-800 p-6" data-testid="admin-tickets-panel">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-semibold">Support Ticket Inbox</h2>
                <p className="text-sm text-slate-400">Reply to users, mark tickets resolved, and track workload.</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
                  <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800" data-testid="ticket-status-filter">
                    <Filter className="w-4 h-4 mr-2" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    <SelectItem value="all">All ({tickets.length})</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={refreshTickets} className="border-zinc-700" data-testid="refresh-tickets-btn">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {filteredTickets.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No tickets in this view.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="admin-tickets-list">
                {filteredTickets.map((t) => {
                  const lastMsg = (t.messages && t.messages.length > 0) ? t.messages[t.messages.length - 1] : null;
                  const statusColor = {
                    open: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
                    in_progress: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                    resolved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                    closed: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
                  }[t.status] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
                  return (
                    <button
                      key={t.ticket_id}
                      onClick={() => setSelectedTicket(t)}
                      data-testid={`ticket-row-${t.ticket_id}`}
                      className="w-full text-left flex items-center gap-4 p-4 rounded-lg bg-zinc-900/50 hover:bg-zinc-900 border border-zinc-800 hover:border-cyan-500/40 transition"
                    >
                      <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-slate-500">{t.ticket_id}</span>
                          <Badge className={`${statusColor} border text-xs`}>{(t.status || 'open').replace('_', ' ')}</Badge>
                          {t.priority && <Badge className="bg-zinc-800 text-slate-300 border-zinc-700 text-xs">{t.priority}</Badge>}
                        </div>
                        <p className="font-medium truncate">{t.subject || 'Support request'}</p>
                        <p className="text-sm text-slate-400 truncate">
                          {t.user_email || t.user_id}
                          {lastMsg ? ` · ${lastMsg.sender === 'support' ? 'You' : 'User'}: ${(lastMsg.message || '').slice(0, 60)}` : ''}
                        </p>
                      </div>
                      <div className="text-xs text-slate-500 flex-shrink-0 hidden md:block">
                        {t.updated_at ? new Date(t.updated_at).toLocaleString() : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Management Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={(open) => {
        setUserDialogOpen(open);
        if (!open) {
          setEditingUserRole('');
          setEditingUserStatus('');
        }
      }}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Manage User
            </DialogTitle>
            <DialogDescription>
              Update user role, status, and subscription
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4 p-4 bg-zinc-900 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full flex items-center justify-center text-xl font-bold">
                  {selectedUser.name?.[0] || 'U'}
                </div>
                <div>
                  <p className="font-semibold text-lg">{selectedUser.name}</p>
                  <p className="text-sm text-slate-400">{selectedUser.email}</p>
                </div>
              </div>

              {/* Role and Status Management */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-400 mb-2 block">User Role</Label>
                  <Select 
                    value={editingUserRole || selectedUser.role || 'user'} 
                    onValueChange={setEditingUserRole}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-zinc-800">
                      {USER_ROLES.map(role => (
                        <SelectItem key={role} value={role}>
                          <span className="flex items-center gap-2 capitalize">
                            {role === 'admin' && <Crown className="w-3 h-3 text-amber-400" />}
                            {role === 'ceo' && <Crown className="w-3 h-3 text-purple-400" />}
                            {role === 'owner' && <Crown className="w-3 h-3 text-blue-400" />}
                            {role === 'developer' && <Settings className="w-3 h-3 text-emerald-400" />}
                            {role === 'support' && <UserCheck className="w-3 h-3 text-cyan-400" />}
                            {role === 'user' && <User className="w-3 h-3 text-slate-400" />}
                            {role}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-400 mb-2 block">Status</Label>
                  <Select 
                    value={editingUserStatus || selectedUser.status || 'active'} 
                    onValueChange={setEditingUserStatus}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-zinc-800">
                      {USER_STATUSES.map(status => (
                        <SelectItem key={status} value={status}>
                          <span className="flex items-center gap-2 capitalize">
                            {status === 'active' && <CheckCircle className="w-3 h-3 text-emerald-400" />}
                            {status === 'inactive' && <XCircle className="w-3 h-3 text-slate-400" />}
                            {status === 'suspended' && <Clock className="w-3 h-3 text-amber-400" />}
                            {status === 'banned' && <Ban className="w-3 h-3 text-red-400" />}
                            {status}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {(editingUserRole || editingUserStatus) && (
                <Button 
                  onClick={() => {
                    const updates = {};
                    if (editingUserRole) updates.role = editingUserRole;
                    if (editingUserStatus) updates.status = editingUserStatus;
                    handleUpdateUser(selectedUser.user_id, updates);
                  }}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Role & Status Changes
                </Button>
              )}

              <div>
                <p className="text-sm text-slate-400 mb-2">Current Plan</p>
                <Badge className={selectedUser.subscription_plan ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-slate-400'}>
                  {selectedUser.subscription_plan || 'Free'}
                </Badge>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Activate Subscription</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" onClick={() => handleActivateSubscription(selectedUser.user_id, 'pro_monthly', 30)}
                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                    Pro Monthly (30 days)
                  </Button>
                  <Button variant="outline" onClick={() => handleActivateSubscription(selectedUser.user_id, 'pro_yearly', 365)}
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
                    Pro Yearly (365 days)
                  </Button>
                </div>
              </div>

              {selectedUser.subscription_plan && (
                <Button variant="outline" onClick={() => handleCancelSubscription(selectedUser.user_id)}
                  className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                  Cancel Subscription
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* KYC Review Dialog */}
      <Dialog open={kycDialogOpen} onOpenChange={setKycDialogOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-4 border-b border-zinc-800 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-400" />
              Review KYC Submission
            </DialogTitle>
          </DialogHeader>
          
          {selectedKyc && (
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* User Info */}
              <div className="p-4 bg-zinc-900 rounded-lg">
                <h3 className="text-sm font-semibold text-slate-300 mb-3 border-b border-zinc-800 pb-2">Applicant Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Full Name</p>
                    <p className="font-semibold text-white">{selectedKyc.full_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Email</p>
                    <p className="text-slate-300 text-sm break-all">{selectedKyc.user_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Country</p>
                    <p className="text-slate-300">{selectedKyc.country}</p>
                  </div>
                  {selectedKyc.date_of_birth && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Date of Birth</p>
                      <p className="text-slate-300">{selectedKyc.date_of_birth}</p>
                    </div>
                  )}
                </div>
                
                <h3 className="text-sm font-semibold text-slate-300 mt-4 mb-3 border-b border-zinc-800 pb-2">Document Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Document Type</p>
                    <p className="text-slate-300 capitalize font-medium">{selectedKyc.document_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Document / ID Number</p>
                    <p className="text-slate-300 font-mono bg-zinc-800 px-2 py-1 rounded inline-block text-sm">
                      {selectedKyc.document_number || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Submitted</p>
                    <p className="text-slate-300 text-sm">{new Date(selectedKyc.submitted_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">KYC ID</p>
                    <p className="text-slate-400 text-xs font-mono">{selectedKyc.kyc_id}</p>
                  </div>
                </div>
              </div>

              {/* Document Images */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> ID Document
                  </p>
                  <div className="aspect-[4/3] bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 relative group">
                    {selectedKyc.document_image && (
                      <>
                        <img 
                          src={`data:image/jpeg;base64,${selectedKyc.document_image}`} 
                          alt="ID Document" 
                          className="w-full h-full object-contain cursor-zoom-in"
                          onClick={() => window.open(`data:image/jpeg;base64,${selectedKyc.document_image}`, '_blank')}
                        />
                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-xs bg-black/70 px-2 py-1 rounded text-white">Click to zoom</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-400 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Selfie
                  </p>
                  <div className="aspect-[4/3] bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 relative group">
                    {selectedKyc.selfie_image && (
                      <>
                        <img 
                          src={`data:image/jpeg;base64,${selectedKyc.selfie_image}`} 
                          alt="Selfie" 
                          className="w-full h-full object-contain cursor-zoom-in"
                          onClick={() => window.open(`data:image/jpeg;base64,${selectedKyc.selfie_image}`, '_blank')}
                        />
                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-xs bg-black/70 px-2 py-1 rounded text-white">Click to zoom</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Verification Checklist */}
              {selectedKyc.status === 'pending' && (
                <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-800">
                  <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Verification Checklist
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-slate-300 font-medium mb-2">Before approving, verify:</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <p className="text-slate-400 text-xs font-semibold uppercase">Face Verification</p>
                        <label className="flex items-start gap-2 text-slate-300 cursor-pointer hover:text-white">
                          <input type="checkbox" className="mt-1 accent-emerald-500" />
                          <span className="text-sm">Selfie face matches ID photo</span>
                        </label>
                        <label className="flex items-start gap-2 text-slate-300 cursor-pointer hover:text-white">
                          <input type="checkbox" className="mt-1 accent-emerald-500" />
                          <span className="text-sm">Selfie is a real photo (not a photo of a screen)</span>
                        </label>
                        <label className="flex items-start gap-2 text-slate-300 cursor-pointer hover:text-white">
                          <input type="checkbox" className="mt-1 accent-emerald-500" />
                          <span className="text-sm">Face is clearly visible in both images</span>
                        </label>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-slate-400 text-xs font-semibold uppercase">Document Verification</p>
                        <label className="flex items-start gap-2 text-slate-300 cursor-pointer hover:text-white">
                          <input type="checkbox" className="mt-1 accent-emerald-500" />
                          <span className="text-sm">Document is not expired</span>
                        </label>
                        <label className="flex items-start gap-2 text-slate-300 cursor-pointer hover:text-white">
                          <input type="checkbox" className="mt-1 accent-emerald-500" />
                          <span className="text-sm">Document looks authentic (no editing)</span>
                        </label>
                        <label className="flex items-start gap-2 text-slate-300 cursor-pointer hover:text-white">
                          <input type="checkbox" className="mt-1 accent-emerald-500" />
                          <span className="text-sm">Name matches the provided name</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-zinc-700">
                      <p className="text-slate-400 text-xs font-semibold uppercase mb-2">Document Number Check</p>
                      <p className="text-slate-400 text-xs">
                        Verify the ID number format matches the country's standard:
                      </p>
                      <ul className="text-xs text-slate-500 mt-1 ml-4 list-disc space-y-1">
                        <li><span className="text-slate-400">Passport:</span> Usually 8-9 alphanumeric characters</li>
                        <li><span className="text-slate-400">Driver's License:</span> Varies by country/state</li>
                        <li><span className="text-slate-400">National ID:</span> Country-specific format</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Rejection Reason Input */}
              {selectedKyc.status === 'pending' && (
                <div>
                  <Label className="text-slate-400">Rejection Reason (required if rejecting)</Label>
                  <Input
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="e.g., Document is blurry, face not visible..."
                    className="bg-zinc-900 border-zinc-800 mt-2"
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs text-slate-500">Quick reasons:</span>
                    {[
                      "Document is blurry or unreadable",
                      "Face doesn't match between selfie and ID",
                      "Document appears to be expired",
                      "Selfie is not clear or partially hidden",
                      "Document looks edited or fake",
                      "ID number not visible",
                      "Wrong document type submitted"
                    ].map((reason) => (
                      <button
                        key={reason}
                        type="button"
                        onClick={() => setRejectionReason(reason)}
                        className="text-xs px-2 py-1 bg-zinc-800 hover:bg-zinc-700 rounded text-slate-400 hover:text-white transition-colors"
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Fixed Action Buttons at Bottom */}
          {selectedKyc?.status === 'pending' && (
            <div className="p-6 pt-4 border-t border-zinc-800 bg-[#121214] shrink-0">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => handleApproveKyc(selectedKyc.kyc_id)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-11"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button 
                  onClick={() => handleRejectKyc(selectedKyc.kyc_id)}
                  variant="outline"
                  disabled={!rejectionReason.trim()}
                  className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10 h-11"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
          
          {/* Already Reviewed Status */}
          {selectedKyc?.status && selectedKyc.status !== 'pending' && (
            <div className="p-6 pt-4 border-t border-zinc-800 bg-[#121214] shrink-0">
              <div className={`p-4 rounded-lg ${
                selectedKyc.status === 'approved' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <p className={`font-semibold ${selectedKyc.status === 'approved' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {selectedKyc.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                </p>
                {selectedKyc.rejection_reason && (
                  <p className="text-sm text-red-300/70 mt-1">Reason: {selectedKyc.rejection_reason}</p>
                )}
                {selectedKyc.reviewed_at && (
                  <p className="text-xs text-slate-500 mt-2">Reviewed: {new Date(selectedKyc.reviewed_at).toLocaleString()}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* KYC PIN Entry Dialog */}
      <Dialog open={kycPinDialogOpen} onOpenChange={setKycPinDialogOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-400" />
              Enter Security PIN
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-400">
              KYC data is encrypted. Enter your admin PIN to decrypt and view sensitive documents.
            </p>
            <div>
              <Label>Admin PIN</Label>
              <Input
                type="password"
                maxLength={8}
                value={kycPin}
                onChange={(e) => setKycPin(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter your 4-8 digit PIN"
                className="bg-zinc-900 border-zinc-800 mt-1 text-center text-2xl tracking-widest"
              />
            </div>
            <Button onClick={accessKycWithPin} className="w-full bg-amber-600 hover:bg-amber-700">
              <Shield className="w-4 h-4 mr-2" />
              Unlock & View
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set KYC PIN Dialog */}
      <Dialog open={setPinDialogOpen} onOpenChange={setSetPinDialogOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              Set KYC Access PIN
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-400">
              Create a PIN to access encrypted KYC documents. This adds an extra layer of security.
            </p>
            <div>
              <Label>New PIN (4-8 digits)</Label>
              <Input
                type="password"
                maxLength={8}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter 4-8 digits"
                className="bg-zinc-900 border-zinc-800 mt-1 text-center text-2xl tracking-widest"
              />
            </div>
            <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-xs text-blue-300">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>This PIN is required every time you access KYC documents. Keep it secure.</span>
            </div>
            <Button onClick={setAdminKycPin} className="w-full bg-blue-600 hover:bg-blue-700">
              <Shield className="w-4 h-4 mr-2" />
              Set PIN
            </Button>
            {hasKycPin && (
              <Button onClick={resetKycPin} variant="outline" className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10">
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset PIN
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(o) => { if (!o) { setSelectedTicket(null); setTicketReply(''); setTicketAttachment(null); } }}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-2xl max-h-[90vh] flex flex-col" data-testid="ticket-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-cyan-400" />
              <span className="truncate">{selectedTicket?.subject || 'Support Ticket'}</span>
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              <span className="font-mono text-xs">{selectedTicket?.ticket_id}</span>
              {selectedTicket?.user_email && <span className="ml-2">· {selectedTicket.user_email}</span>}
              {selectedTicket?.status && <span className="ml-2 uppercase text-xs">· {selectedTicket.status.replace('_', ' ')}</span>}
            </DialogDescription>
          </DialogHeader>

          {selectedTicket && (
            <>
              <div className="flex-1 overflow-y-auto py-3 space-y-3 pr-1" data-testid="ticket-messages">
                {(selectedTicket.messages || []).map((m, idx) => {
                  const isSupport = m.sender === 'support' || m.sender === 'admin';
                  return (
                    <div key={idx} className={`flex ${isSupport ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg px-4 py-3 ${isSupport ? 'bg-cyan-500/15 border border-cyan-500/30' : 'bg-zinc-900 border border-zinc-800'}`}>
                        <div className="flex items-center gap-2 mb-1 text-xs text-slate-400">
                          <span className="font-medium">{isSupport ? (m.sender_name || 'TradeForge Support') : (m.sender_name || 'User')}</span>
                          <span>·</span>
                          <span>{m.timestamp ? new Date(m.timestamp).toLocaleString() : ''}</span>
                        </div>
                        {m.message && <p className="text-sm whitespace-pre-wrap break-words">{m.message}</p>}
                        {m.attachment && m.attachment.file_id && (
                          <a
                            href={`${API}/files/${m.attachment.file_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 underline"
                          >
                            <FileText className="w-3 h-3" />
                            {m.attachment.filename || 'Attachment'}
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(!selectedTicket.messages || selectedTicket.messages.length === 0) && (
                  <p className="text-slate-500 text-sm text-center py-6">No messages in this ticket yet.</p>
                )}
              </div>

              <div className="border-t border-zinc-800 pt-3 space-y-3">
                <Textarea
                  data-testid="ticket-reply-input"
                  placeholder="Type your reply to the user…"
                  value={ticketReply}
                  onChange={(e) => setTicketReply(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 min-h-[90px]"
                />
                {ticketAttachment && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-sm" data-testid="ticket-attachment-preview">
                    <Paperclip className="w-4 h-4 text-cyan-400" />
                    <span className="truncate flex-1">{ticketAttachment.name}</span>
                    <span className="text-xs text-slate-400">{(ticketAttachment.size / 1024).toFixed(1)} KB</span>
                    <button
                      onClick={() => setTicketAttachment(null)}
                      className="text-slate-400 hover:text-red-400"
                      data-testid="ticket-attachment-remove"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex gap-2 items-center flex-wrap">
                    <input
                      id="admin-ticket-file-input"
                      data-testid="admin-ticket-file-input"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (!f) return;
                        if (f.size > 10 * 1024 * 1024) {
                          toast.error('File must be under 10 MB');
                          return;
                        }
                        setTicketAttachment(f);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-zinc-700"
                      onClick={() => document.getElementById('admin-ticket-file-input').click()}
                      data-testid="ticket-attach-btn"
                    >
                      <Paperclip className="w-4 h-4 mr-1" />Attach
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-zinc-700"
                      onClick={() => handleTicketStatusChange('in_progress')}
                      data-testid="ticket-mark-inprogress-btn"
                    >
                      <Clock className="w-4 h-4 mr-1" />In Progress
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-emerald-600/60 text-emerald-400 hover:bg-emerald-500/10"
                      onClick={() => handleTicketStatusChange('resolved')}
                      data-testid="ticket-mark-resolved-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />Resolve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-zinc-700 text-slate-400"
                      onClick={() => handleTicketStatusChange('closed')}
                      data-testid="ticket-mark-closed-btn"
                    >
                      <XCircle className="w-4 h-4 mr-1" />Close
                    </Button>
                  </div>
                  <Button
                    onClick={handleTicketReply}
                    disabled={ticketSending || (!ticketReply.trim() && !ticketAttachment)}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="ticket-send-reply-btn"
                  >
                    {ticketSending ? (ticketUploading ? 'Uploading…' : 'Sending…') : (ticketAttachment ? 'Send Attachment' : 'Send Reply')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
